import React, { createContext, useContext, useState, useEffect } from "react";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  deleteDoc,
  setDoc,
  getDoc,
} from "firebase/firestore";
import { db, auth } from "../firebase";

// Create the context
const PdfContext = createContext();

// Provider component
export const PdfProvider = ({ children }) => {
  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sigUrl, setSigUrl] = useState(null);

  // Function to fetch PDFs from Firebase
  const fetchPdfs = async () => {
    setLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        console.error("No user logged in.");
        setLoading(false);
        return;
      }
      const querySnapshot = await getDocs(
        collection(db, "userFiles", user.uid, "files")
      );
      const pdfData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPdfs(pdfData);

      await fetchUserSettings(user.uid);
    } catch (error) {
      console.error("Error fetching PDFs from Firebase:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserSettings = async (userId) => {
    try {
      if (!userId) throw new Error("User not logged in");

      // Reference to the settings document
      const settingsRef = doc(db, "userFiles", userId);
      const docSnap = await getDoc(settingsRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        setSigUrl(data.sigUrl);
      }
    } catch (error) {
      console.error("Error in fetching signature url:", error);
    }
  };

  // Optionally, call fetchPdfs when the provider mounts
  useEffect(() => {
    fetchPdfs();
  }, []);

  // Function to update a PDF in the state
  const updatePdfInState = (pdf) => {
    setPdfs((prevPdfs) =>
      prevPdfs.map((item) => (item.id === pdf.id ? pdf : item))
    );
  };

  // Function to remove a PDF from state
  const removePdfFromState = (id) => {
    setPdfs((prevPdfs) => prevPdfs.filter((pdf) => pdf.id !== id));
  };

  // Function to save a PDF in the Firestore collection
  const savePdf = async (id, pdfData, userId) => {
    setLoading(true);
    try {
      if (!id) {
        console.warn("PDF does not exist in database");
        return null;
      }
      // update existing
      const docRef = doc(db, "userFiles", userId, "files", id);
      await updateDoc(docRef, pdfData);
      const newPdf = { id, ...pdfData };
      updatePdfInState(newPdf);
      return newPdf;
    } catch (error) {
      console.error("Error saving PDF to Firestore:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const saveSignature = async (sigUrl, userId) => {
    setLoading(true);
    try {
      if (!userId) {
        console.warn("Data not found.");
        return null;
      }
      // update existing
      const docRef = doc(db, "userFiles", userId);
      await setDoc(
        docRef,
        {
          sigUrl: sigUrl,
        },
        { merge: true }
      );

      setSigUrl(sigUrl);
      return sigUrl;
    } catch (error) {
      console.error("Error saving signature URL to Firestore:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Function to delete a PDF from Firestore and update state
  const deletePdf = async (id, userId) => {
    try {
      setLoading(true);
      const docRef = doc(db, "userFiles", userId, "files", id);
      await deleteDoc(docRef);
      removePdfFromState(id);
      // console.log(`PDF with id ${id} deleted successfully.`);
    } catch (error) {
      console.error("Error deleting PDF from Firestore:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <PdfContext.Provider
      value={{
        pdfs,
        sigUrl,
        setSigUrl,
        loading,
        fetchPdfs,
        savePdf,
        saveSignature,
        deletePdf,
      }}
    >
      {children}
    </PdfContext.Provider>
  );
};

// Custom hook for consuming the context
export const usePdf = () => useContext(PdfContext);
